import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, ExternalLink } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { SocialShare } from "@/components/site/SocialShare";

const PROMO_SITES = [
  ["Spotify for Artists", "Proponi il brano alle playlist editoriali (almeno 7 giorni prima dell'uscita)", "https://artists.spotify.com"],
  ["Apple Music for Artists", "Profilo, statistiche e promo su Apple Music", "https://artists.apple.com"],
  ["SubmitHub", "Invia a blog, curatori di playlist e canali YouTube", "https://www.submithub.com"],
  ["Groover", "Invia a radio, media e curatori in tutta Europa", "https://groover.co"],
  ["LabelRadar", "Visibilità verso etichette e promoter dance", "https://www.labelradar.com"],
  ["Inflyte", "Promo pool per DJ: manda il brano in anteprima ai DJ", "https://www.inflyteapp.com"],
  ["Hypeddit", "Download gate e campagne per crescere i follower", "https://hypeddit.com"],
  ["Musosoup", "Campagne verso blog e playlist indipendenti", "https://www.musosoup.com"],
  ["Daily Playlists", "Playlist Spotify curate per genere", "https://www.dailyplaylists.com"],
  ["Repost Exchange", "Scambio di repost su SoundCloud", "https://repostexchange.com"],
  ["Reddit r/House", "Community house", "https://www.reddit.com/r/House"],
  ["Reddit r/Techno", "Community techno", "https://www.reddit.com/r/Techno"],
];

const tag = (s = "") => `#${s.replace(/&/g, "and").replace(/[^a-zA-Z0-9]/g, "")}`;

const caption = (r, lang, url) => {
  const genre = r.genre || r.tracks?.find((t) => t.genre)?.genre || "";
  const tags = [tag("TheGrooveAnomaly"), genre && tag(genre), tag("NewMusic"), tag("ElectronicMusic"), tag(r.artist_name || ""), "#IndieLabel"].filter((t) => t && t !== "#").join(" ");
  return lang === "it"
    ? `🔥 FUORI ORA: "${r.title}" di ${r.artist_name || "The Groove Anomaly"} su THE GROOVE ANOMALY records${genre ? ` · ${genre}` : ""}.\n🎧 Ascolta e condividi: ${url}\n\n${tags}`
    : `🔥 OUT NOW: "${r.title}" by ${r.artist_name || "The Groove Anomaly"} on THE GROOVE ANOMALY records${genre ? ` · ${genre}` : ""}.\n🎧 Listen & share: ${url}\n\n${tags}`;
};

export const AdminPromo = () => {
  const [list, setList] = useState([]);
  const [rid, setRid] = useState("");
  const [lang, setLang] = useState("it");
  useEffect(() => { api.get("/admin/releases").then((r) => { const pub = r.data.filter((x) => x.published); setList(pub); if (pub[0]) setRid(pub[0].id); }); }, []);
  const rel = list.find((r) => r.id === rid);
  const url = rel ? `${window.location.origin}/api/share/release/${rel.id}` : "";
  const text = useMemo(() => (rel ? caption(rel, lang, url) : ""), [rel, lang, url]);
  const copy = async () => { await navigator.clipboard.writeText(text); toast.success("Testo copiato"); };

  return (
    <div className="space-y-8" data-testid="admin-promo">
      <div>
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Promozione</h2>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">Scegli un'uscita: il testo e gli hashtag si creano da soli. Condividila con un clic sui social e inviala ai siti di promozione qui sotto.</p>
      </div>
      {!list.length ? <p className="text-sm text-zinc-400 border border-dashed border-white/15 p-10 text-center">Pubblica prima un'uscita per promuoverla.</p> : (
        <>
          <div className="flex flex-wrap gap-3">
            <select value={rid} onChange={(e) => setRid(e.target.value)} className="rounded-full bg-[#262b42] border border-white/15 px-4 h-11 text-sm" data-testid="promo-release-select">
              {list.map((r) => <option key={r.id} value={r.id}>{r.title} — {r.artist_name}</option>)}
            </select>
            <div className="inline-flex rounded-full border border-white/20 p-1">
              {[["it", "Italiano"], ["en", "English"]].map(([k, l]) => <button key={k} onClick={() => setLang(k)} className={`rounded-full px-4 py-1.5 text-xs font-bold ${lang === k ? "bg-emerald-500 text-[#151827]" : "text-zinc-300"}`} data-testid={`promo-lang-${k}`}>{l}</button>)}
            </div>
          </div>
          <div className="grid md:grid-cols-[200px_1fr] gap-5">
            {rel?.cover_url ? (
              <div className="space-y-2">
                <img src={fileUrl(rel.cover_url)} alt="" className="w-full aspect-square object-cover rounded-xl border border-white/10" />
                <a href={fileUrl(rel.cover_url)} download className="inline-flex items-center gap-1 text-xs text-emerald-300" data-testid="promo-cover-download"><Download className="h-3.5 w-3.5" /> Scarica copertina</a>
              </div>
            ) : <div />}
            <div className="space-y-3">
              <textarea readOnly value={text} rows={7} className="w-full rounded-xl bg-[#262b42] border border-white/15 p-4 text-sm" data-testid="promo-caption" />
              <button onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#151827] px-5 py-2.5 text-sm font-bold" data-testid="promo-copy-btn"><Copy className="h-4 w-4" /> Copia testo (per Instagram e TikTok)</button>
              <SocialShare releaseId={rel.id} text={`${rel.title} — ${rel.artist_name}`} prefix="promo-share" />
            </div>
          </div>
        </>
      )}
      <div>
        <p className="font-mono-tga text-[10px] uppercase tracking-[0.2em] text-hot mb-3">Siti di promozione musicale</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PROMO_SITES.map(([n, d, u]) => (
            <a key={n} href={u} target="_blank" rel="noopener noreferrer" className="lift rounded-xl border border-white/10 bg-[#1e2236] p-4 block" data-testid={`promo-site-${n.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="font-semibold flex items-center gap-1.5">{n} <ExternalLink className="h-3.5 w-3.5 text-zinc-400" /></p>
              <p className="text-xs text-zinc-400 mt-1">{d}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
